import React, { useState, useEffect, useRef } from 'react';
import { FiFileText, FiUser, FiHelpCircle } from 'react-icons/fi';
import { BsCurrencyRupee } from 'react-icons/bs';
import { MdOutlineDescription, MdOutlineNumbers } from 'react-icons/md';
import { BsListCheck } from 'react-icons/bs';
import { RiFileTextLine } from 'react-icons/ri';
import { TbBuildingStore } from 'react-icons/tb';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import InputField from '../components/ui/InputField';
import ImageUploader from '../components/ui/ImageUploader';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { createProject, loadProjects } from '../redux/actions/projectAction';
import { clearProjectsCache, clearCache, clearStatusData } from '../redux/slices/projectSlice';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { getValidStatusTransitions } from '../utils/workflowValidation';
import StatusTransitionHelpModal from '../components/ui/StatusTransitionHelpModal';
import { hasFinancePermission } from '../utils/permissionUtils';
import { forceRefreshCustomers } from '../redux/actions/forceRefreshCustomers';
import { forceRefreshEmployees } from '../redux/actions/forceRefreshEmployees';
import { clearUserProjectsCache } from '../redux/slices/userProjectsSlice';
import { refreshUserProjects } from '../redux/actions/userProjectsAction';
import { store } from '../redux/store';

// Define the project data interface
interface ProjectData {
  projectName: string;
  articleNumber: string;
  customerId: string;
  customerName: string;
  assignedEmployeeId: string;
  assignedEmployeeName: string;
  projectDescription: string;
  projectRemarks: string;
  projectStatus: string;
  projectCost: string;
  projectImage: File | null;
  isValid: boolean;
}

const AddProject = () => {
  // Project details state
  const [projectName, setProjectName] = useState('');
  const [articleNumber, setArticleNumber] = useState('');
  const [customer, setCustomer] = useState('');
  const [assignedEmployee, setAssignedEmployee] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectRemarks, setProjectRemarks] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [projectCost, setProjectCost] = useState('0');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useAppSelector(state => state.projects);

  // Add search states - MOVED UP to fix the initialization error
  const [customerSearch, setCustomerSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');

  // States to control dropdown visibility
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  // Project image state
  const [projectImage, setProjectImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // State for status transition help modal
  const [showStatusHelpModal, setShowStatusHelpModal] = useState(false);

  // Get customer and employee data from Redux store
  const { customers, employees } = useAppSelector(state => state.users);
  // Get current user data from auth state
  const { user } = useAppSelector(state => state.auth);

  // Define isAdmin at component level so it's available throughout the component
  const isAdmin = user?.designation === 'superAdmin' || user?.designation === 'admin';

  // Add this check near the other permission checks
  const hasFinanceAccess = hasFinancePermission(user);

  // Check permission and redirect if user doesn't have projectWrite permission
  useEffect(() => {
    // Function to check if user has projectWrite permission
    const checkProjectWritePermission = () => {
      // If user is superAdmin, they have all permissions
      if (user?.designation === 'superAdmin') {
        return true;
      }

      // If no user or no permissions, deny access
      if (!user || !user.permissions) {
        return false;
      }

      let hasProjectWritePermission = false;

      // Check if permissions is an object with numeric keys (like {0: {...}})
      if (typeof user.permissions === 'object' && '0' in user.permissions) {
        //@ts-ignore
        hasProjectWritePermission = Boolean(user.permissions[0].projectWrite);
      }
      // Check if permissions is an array
      else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
        //@ts-ignore
        hasProjectWritePermission = Boolean(user.permissions[0].projectWrite);
      }
      // Direct object access if neither of the above
      else if (typeof user.permissions === 'object') {
        //@ts-ignore
        hasProjectWritePermission = Boolean(user.permissions.projectWrite);
      }

      return hasProjectWritePermission;
    };

    // If user doesn't have permission, redirect and show toast
    if (!checkProjectWritePermission()) {
      toast.error("You don't have permission to create projects");
      navigate('/projects');
    }
  }, [user, navigate]);

  // Check if the project is under revision based on URL parameters or state
  const queryParams = new URLSearchParams(location.search);
  const isRevisionProject = queryParams.get('revision') === 'true' || location.state?.isRevision;

  // Add an effect to force refresh customers and employees when component mounts
  useEffect(() => {
    // Fetch fresh customer data when the component mounts
    dispatch(forceRefreshCustomers());
    console.log('Refreshed customers data on component mount');

    // Fetch fresh employee data when the component mounts
    dispatch(forceRefreshEmployees());
    console.log('Refreshed employees data on component mount');
  }, [dispatch]);

  // Function to format status values for display
  const formatStatus = (status: string): string => {
    if (!status) return '';

    // Replace underscores with spaces
    let formattedStatus = status.replace(/_/g, ' ');

    // Convert to title case (capitalize first letter of each word)
    formattedStatus = formattedStatus
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle special cases
    switch (status.toUpperCase()) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PAYMENT_RECEIVED':
        return 'Payment Received';
      default:
        return formattedStatus;
    }
  };

  // Define project status options based on schema.prisma
  const allProjectStatuses = [
    'UNASSIGNED',
    'ASSIGNED',
    'IN_PROGRESS',
    'REVIEW',
    'DELIVERED',
    'BILLED',
    'PAYMENT_RECEIVED',
    'NON_ACTIVE',
  ];

  // Function to get filtered status options based on user role and permissions
  const getStatusOptions = () => {
    // Get permissions from user object, ensuring we handle different response formats
    // We need to use type assertion since the permissions structure can vary
    //@ts-ignore
    const permissions = user?.permissions || {};

    // If admin, show all statuses
    if (isAdmin) {
      console.log('User is admin, showing all statuses');
      return allProjectStatuses;
    }

    // Map statuses to permissions
    const statusPermissionMap: Record<string, string> = {
      ASSIGNED: 'canAssignProject',
      IN_PROGRESS: 'canInProgress',
      REVIEW: 'canReview',
      DELIVERED: 'canDeliverProject',
      BILLED: 'canBillProject',
      HOLD: 'canHoldProject',
      CANCELLED: 'canCancelProject',
      PAYMENT_RECEIVED: 'canReceivePayment',
      REOPEN: 'canReopenProject',
    };

    // Filter statuses based on user permissions
    const filteredStatuses = allProjectStatuses.filter(status => {
      // UNASSIGNED is shown to everyone
      if (status === 'UNASSIGNED') return true;

      // NON_ACTIVE isn't associated with a specific permission, but is generally admin-only
      if (status === 'NON_ACTIVE') return false;

      // Check if user has permission for this status
      const permissionKey = statusPermissionMap[status];

      if (!permissionKey) return false;

      // Check if permission exists and is true
      return permissions[permissionKey] === true;
    });

    console.log('Filtered statuses for employee:', filteredStatuses);
    return filteredStatuses;
  };

  // Get filtered statuses based on user permissions
  const statusOptions = getStatusOptions();

  // Function to validate project data
  const validateProjectData = (data: ProjectData): boolean => {
    // Add your validation logic here
    const isValid = !!(
      data.projectName &&
      data.articleNumber &&
      data.customerId &&
      data.assignedEmployeeId &&
      data.projectDescription &&
      data.projectStatus
    );

    // Console log all data when all fields are valid
    if (isValid) {
      console.log('Project Data:', data);
    }

    return isValid;
  };

  // Combined project data state - MOVED down after all individual states are declared
  const [projectData, setProjectData] = useState<ProjectData>({
    projectName: '',
    articleNumber: '',
    customerId: '',
    customerName: '',
    assignedEmployeeId: '',
    assignedEmployeeName: '',
    projectDescription: '',
    projectRemarks: '',
    projectStatus: '',
    projectCost: '',
    projectImage: null,
    isValid: false,
  });

  // Function to update the combined project data state
  const updateProjectData = (field: keyof ProjectData, value: any) => {
    setProjectData(prevData => {
      const newData = { ...prevData, [field]: value };
      // Validate data and update isValid flag
      newData.isValid = validateProjectData(newData);
      return newData;
    });
  };

  const handleImageUpload = (file: File) => {
    console.log(
      'Image upload triggered with file:',
      file.name,
      'type:',
      file.type,
      'size:',
      file.size
    );
    setProjectImage(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result as string);
      console.log('Image preview generated successfully');
    };
    reader.readAsDataURL(file);

    // Update combined project data
    updateProjectData('projectImage', file);
  };

  const handleImageRemove = () => {
    console.log('Image removed');
    setProjectImage(null);
    setImagePreview(null);

    // Update combined project data
    updateProjectData('projectImage', null);
  };

  // Refs for detecting outside clicks
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);

  // Format customer and employee data with filtering based on search
  const customerOptions = [...customers] // Create a copy of the array before sorting
    .sort((a, b) => {
      // If search is empty, sort alphabetically
      if (!customerSearch) return a.companyName.localeCompare(b.companyName);

      const aStartsWithSearch = a.companyName
        .toLowerCase()
        .startsWith(customerSearch.toLowerCase());
      const bStartsWithSearch = b.companyName
        .toLowerCase()
        .startsWith(customerSearch.toLowerCase());

      // Sort by whether the item starts with the search term
      if (aStartsWithSearch && !bStartsWithSearch) return -1;
      if (!aStartsWithSearch && bStartsWithSearch) return 1;

      // If both start with search or neither starts with search, sort by whether it contains the search term
      const aContainsSearch = a.companyName.toLowerCase().includes(customerSearch.toLowerCase());
      const bContainsSearch = b.companyName.toLowerCase().includes(customerSearch.toLowerCase());

      if (aContainsSearch && !bContainsSearch) return -1;
      if (!aContainsSearch && bContainsSearch) return 1;

      // If both match equally, sort alphabetically
      return a.companyName.localeCompare(b.companyName);
    })
    .map(c => c.companyName);

  const employeeOptions = [...employees] // Create a copy of the array before sorting
    .filter(e => {
      // Always include the current user (You option) - never filter it out
      if (e.id === user?.id) return true;

      // If this employee is selected and matches the search, include them
      if (assignedEmployee && e.id === assignedEmployee) {
        // Check if search matches the selected employee
        if (employeeSearch) {
          const searchLower = employeeSearch.toLowerCase();
          const nameLower = e.fullname.toLowerCase();
          // Handle "You (name)" pattern for current user
          if (e.id === user?.id) {
            const youPattern = `you (${nameLower})`;
            return (
              nameLower.includes(searchLower) ||
              youPattern.includes(searchLower) ||
              searchLower === 'you'
            );
          }
          return nameLower.includes(searchLower);
        }
        return false; // If no search and employee is selected, don't show in dropdown
      }

      // Apply search filter for non-selected employees
      if (employeeSearch) {
        const searchLower = employeeSearch.toLowerCase();
        const nameLower = e.fullname.toLowerCase();
        // Handle "You (name)" pattern for current user
        if (e.id === user?.id) {
          const youPattern = `you (${nameLower})`;
          return (
            nameLower.includes(searchLower) ||
            youPattern.includes(searchLower) ||
            searchLower === 'you'
          );
        }
        return nameLower.includes(searchLower);
      }

      return true;
    })
    .sort((a, b) => {
      // If search is empty, sort alphabetically
      if (!employeeSearch) return a.fullname.localeCompare(b.fullname);

      const aStartsWithSearch = a.fullname.toLowerCase().startsWith(employeeSearch.toLowerCase());
      const bStartsWithSearch = b.fullname.toLowerCase().startsWith(employeeSearch.toLowerCase());

      // Sort by whether the item starts with the search term
      if (aStartsWithSearch && !bStartsWithSearch) return -1;
      if (!aStartsWithSearch && bStartsWithSearch) return 1;

      // If both start with search or neither starts with search, sort by whether it contains the search term
      const aContainsSearch = a.fullname.toLowerCase().includes(employeeSearch.toLowerCase());
      const bContainsSearch = b.fullname.toLowerCase().includes(employeeSearch.toLowerCase());

      if (aContainsSearch && !bContainsSearch) return -1;
      if (!aContainsSearch && bContainsSearch) return 1;

      // If both match equally, sort alphabetically
      return a.fullname.localeCompare(b.fullname);
    })
    .map(e => e.fullname);

  // Handle customer search input
  const handleCustomerSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setShowCustomerDropdown(true);

    // If customers array is empty or very small, force a refresh
    if (customers.length < 2) {
      console.log('Few or no customers found in state, refreshing...');
      dispatch(forceRefreshCustomers());
    }

    // Also update the main customer selection if exact match
    const exactMatch = customers.find(c => c.companyName.toLowerCase() === value.toLowerCase());
    if (exactMatch) {
      setCustomer(exactMatch.id);
      updateProjectData('customerName', value);
      updateProjectData('customerId', exactMatch.id);
    }
  };

  // Handle employee search input
  const handleEmployeeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmployeeSearch(value);
    setShowEmployeeDropdown(true);

    // If employees array is empty or very small, force a refresh
    if (employees.length < 2) {
      console.log('Few or no employees found in state, refreshing...');
      dispatch(forceRefreshEmployees());
    }

    // Check if input matches "You" or "You (" pattern and convert to current user
    if ((value.toLowerCase() === 'you' || value.toLowerCase().startsWith('you (')) && user?.id) {
      const currentUser = employees.find(e => e.id === user.id);
      if (currentUser) {
        // Update data with current user's info
        setAssignedEmployee(currentUser.id);
        updateProjectData('assignedEmployeeName', currentUser.fullname);
        updateProjectData('assignedEmployeeId', currentUser.id);
        return;
      }
    }

    // Handle "You (name)" format input
    const youPattern = /^You \((.+)\)$/;
    const youMatch = value.match(youPattern);
    if (youMatch && user?.id) {
      const currentUser = employees.find(e => e.id === user.id);
      if (currentUser && currentUser.fullname === youMatch[1]) {
        setAssignedEmployee(currentUser.id);
        updateProjectData('assignedEmployeeName', currentUser.fullname);
        updateProjectData('assignedEmployeeId', currentUser.id);
        return;
      }
    }

    // Handle normal employee search
    const exactMatch = employees.find(e => e.fullname.toLowerCase() === value.toLowerCase());
    if (exactMatch) {
      setAssignedEmployee(exactMatch.id);
      updateProjectData('assignedEmployeeName', value);
      updateProjectData('assignedEmployeeId', exactMatch.id);
    }
  };

  // Update combined project data whenever individual fields change
  useEffect(() => {
    setProjectData(prevData => ({
      ...prevData,
      projectName,
      articleNumber,
      customerId: customer,
      customerName: customerSearch,
      assignedEmployeeId: assignedEmployee,
      assignedEmployeeName: employeeSearch,
      projectDescription,
      projectRemarks,
      projectStatus,
      projectCost,
      projectImage,
      isValid: validateProjectData({
        ...prevData,
        projectName,
        articleNumber,
        customerId: customer,
        customerName: customerSearch,
        assignedEmployeeId: assignedEmployee,
        assignedEmployeeName: employeeSearch,
        projectDescription,
        projectRemarks,
        projectStatus,
        projectCost,
        projectImage,
      }),
    }));
  }, [
    projectName,
    articleNumber,
    customer,
    customerSearch,
    assignedEmployee,
    employeeSearch,
    projectDescription,
    projectRemarks,
    projectStatus,
    projectCost,
    projectImage,
  ]);

  const handleSubmit = async () => {
    // Check if form data is valid
    if (!projectData.isValid) {
      toast.error('Please fill all required fields');
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting || loading) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the data for the API
      const formattedData = {
        projectName: projectData.projectName,
        projectNumber: projectData.articleNumber,
        projectDescription: projectData.projectDescription,
        projectRemark: projectData.projectRemarks,
        ClientId: projectData.customerId,
        projectAmount: parseFloat(projectData.projectCost) || 0,
        projectImage: projectData.projectImage, // Add the image file directly
        projectAssigned: [
          {
            assignedToEmployee: projectData.assignedEmployeeId,
            assignedDateAndTime: new Date().toISOString(),
            status: projectData.projectStatus,
            note: 'Initial project assignment',
          },
        ],
      };

      // Dispatch the create project action
      const resultAction = await dispatch(createProject(formattedData));

      if (createProject.fulfilled.match(resultAction)) {
        // Success notification
        toast.success('Project created successfully!');

        // Clear all caches to ensure fresh data is fetched
        dispatch(clearProjectsCache());
        dispatch(clearCache());
        dispatch(clearStatusData());
        dispatch(clearUserProjectsCache());

        // Refresh projects data to ensure consistent structure
        await dispatch(loadProjects({ page: 1, limit: 10 }));

        // Get the current user ID to refresh user projects
        const { auth } = store.getState();
        if (auth.user?.id) {
          // Refresh user projects data to ensure latest data is fetched
          await dispatch(
            refreshUserProjects({
              userId: auth.user.id,
              page: 1,
              limit: 10,
            })
          );
        }

        // Navigate to projects page immediately after success
        navigate('/projects');
      } else if (createProject.rejected.match(resultAction)) {
        // Error notification with the error message from the API
        toast.error((resultAction.payload as string) || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close customer dropdown if click is outside
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }

      // Close employee dropdown if click is outside
      if (
        employeeDropdownRef.current &&
        !employeeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowEmployeeDropdown(false);
      }
    };

    // Handle Escape key press
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCustomerDropdown(false);
        setShowEmployeeDropdown(false);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  return (
    <ComponentWrapper icon={<FiFileText />} title="Add New Project">
      <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 relative">
        {/* Loading overlay */}
        {(isSubmitting || loading) && (
          <div className="absolute inset-0 bg-white bg-opacity-80 z-10 flex flex-col items-center justify-center rounded-lg">
            <img src="/src/assets/loading.gif" alt="Loading" className="w-16 h-16" />
            <p className="mt-4 text-gray-700 font-medium">Creating your project...</p>
          </div>
        )}

        <div className="flex flex-col gap-4 sm:gap-5 md:gap-6 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <InputField
              label="Project Name"
              type="text"
              placeholder="Enter Project Name"
              value={projectName}
              onChange={e => {
                setProjectName(e.target.value);
                updateProjectData('projectName', e.target.value);
              }}
              icon={<FiFileText />}
              labelClassName="text-[#8D99A5] text-xs"
            />
            <InputField
              label="Article Number"
              type="text"
              placeholder="Enter Article Number"
              value={articleNumber}
              onChange={e => {
                setArticleNumber(e.target.value);
                updateProjectData('articleNumber', e.target.value);
              }}
              icon={<MdOutlineNumbers />}
              labelClassName="text-[#8D99A5] text-xs"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative" ref={customerDropdownRef}>
              <div className="relative">
                <InputField
                  label="Company Name"
                  type="text"
                  placeholder="Search Company..."
                  value={customerSearch}
                  onChange={handleCustomerSearchChange}
                  icon={<TbBuildingStore />}
                  labelClassName="text-[#8D99A5] text-xs"
                  onClick={() => setShowCustomerDropdown(true)} // This will show dropdown when clicking anywhere in the input
                  onFocus={() => {
                    // Refresh customers when the dropdown is focused
                    dispatch(forceRefreshCustomers());
                    setShowCustomerDropdown(true);
                  }}
                />
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/3 cursor-pointer z-10"
                  onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 111.414 1.414l-4 4a1 1 01-1.414 0l-4-4a1 1 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              {showCustomerDropdown && customerOptions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {customerOptions.map((name, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setCustomerSearch(name);
                        const selectedCustomer = customers.find(c => c.companyName === name);
                        if (selectedCustomer) {
                          setCustomer(selectedCustomer.id);
                          updateProjectData('customerName', name);
                          updateProjectData('customerId', selectedCustomer.id);
                        }
                        setShowCustomerDropdown(false);
                      }}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={employeeDropdownRef}>
              <div className="relative">
                <InputField
                  label="Assigned to Employee"
                  type="text"
                  placeholder="Search Employee..."
                  value={employeeSearch}
                  onChange={handleEmployeeSearchChange}
                  icon={<FiUser />}
                  labelClassName="text-[#8D99A5] text-xs"
                  onClick={() => setShowEmployeeDropdown(true)} // This will show dropdown when clicking anywhere in the input
                  onFocus={() => {
                    // Refresh employees when the dropdown is focused
                    dispatch(forceRefreshEmployees());
                    setShowEmployeeDropdown(true);
                  }}
                />
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/3 cursor-pointer z-10"
                  onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 111.414 1.414l-4 4a1 1 01-1.414 0l-4-4a1 1 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              {showEmployeeDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {/* Self Assign option at the top - only show for admin/superAdmin */}
                  {isAdmin && (
                    <div
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer font-medium text-lime-700 border-b border-gray-200"
                      onClick={() => {
                        // Find current user's full name for display
                        const currentUser = employees.find(e => e.id === user?.id);
                        const displayText = currentUser
                          ? `You (${currentUser.fullname})`
                          : 'Self Assign';
                        setEmployeeSearch(displayText);

                        // Get the current user ID from auth state - always use the id property
                        // since that's the one guaranteed to exist in the User type
                        const currentUserId = user?.id;

                        // Ensure currentUserId is a string
                        const userIdString = String(currentUserId || '');

                        console.log('Self Assign clicked - User data:', user);
                        console.log('Current user ID for self-assignment:', userIdString);

                        setAssignedEmployee(userIdString);
                        updateProjectData(
                          'assignedEmployeeName',
                          currentUser?.fullname || 'Self Assign'
                        );
                        updateProjectData('assignedEmployeeId', userIdString);
                        setShowEmployeeDropdown(false);
                      }}
                    >
                      {user && employees.find(e => e.id === user.id)
                        ? `You (${employees.find(e => e.id === user.id)?.fullname})`
                        : 'Self Assign'}
                    </div>
                  )}
                  {employeeOptions.length > 0 ? (
                    employeeOptions.map((name, index) => {
                      const selectedEmployee = employees.find(e => e.fullname === name);
                      // Check if this employee is the logged-in user
                      const isCurrentUser = selectedEmployee && selectedEmployee.id === user?.id;
                      return (
                        <div
                          key={index}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            // Display "You (name)" format when current user is selected
                            const displayText = isCurrentUser ? `You (${name})` : name;
                            setEmployeeSearch(displayText);
                            if (selectedEmployee) {
                              setAssignedEmployee(selectedEmployee.id);
                              updateProjectData('assignedEmployeeName', name);
                              updateProjectData('assignedEmployeeId', selectedEmployee.id);
                            }
                            setShowEmployeeDropdown(false);
                          }}
                        >
                          {isCurrentUser ? `You (${name})` : name}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No employees found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <InputField
            label="Project Description"
            type="textarea"
            placeholder="Enter Project Description"
            value={projectDescription}
            onChange={e => {
              setProjectDescription(e.target.value);
              updateProjectData('projectDescription', e.target.value);
            }}
            icon={<MdOutlineDescription />}
            className="min-h-[80px]"
            labelClassName="text-[#8D99A5] text-xs"
          />

          <InputField
            label="Select Service"
            type="select"
            placeholder="Select a service"
            value={projectRemarks}
            options={[
              'Flow Simulation - Moldex',
              'Flow Simulation - MF',
              'FEA Simulation',
              'To be Selected',
              'Moldflow Training',
              'Casting Simulation',
              'Internal Billing',
              'Product Sale',
            ]}
            onChange={e => {
              setProjectRemarks(e.target.value);
              updateProjectData('projectRemarks', e.target.value);
            }}
            icon={<RiFileTextLine />}
            labelClassName="text-[#8D99A5] text-xs"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="flex-grow">
                  <InputField
                    label={`Project Status ${isRevisionProject ? `(Under revision)` : ``}`}
                    type="select"
                    placeholder="Select Status"
                    value={projectStatus}
                    onChange={e => {
                      setProjectStatus(e.target.value);
                      updateProjectData('projectStatus', e.target.value);
                    }}
                    options={getValidStatusTransitions(statusOptions, null, isAdmin).map(
                      statusOption => {
                        // Format the status for display in the dropdown
                        let displayValue = formatStatus(statusOption.value);

                        // Define colors for each status
                        let bgColor = 'bg-white';
                        let textColor = 'text-gray-700';

                        // Assign different colors based on status
                        switch (statusOption.value) {
                          case 'UNASSIGNED':
                            bgColor = 'bg-gray-100';
                            textColor = 'text-gray-700';
                            break;
                          case 'ASSIGNED':
                            bgColor = 'bg-blue-50';
                            textColor = 'text-blue-700';
                            break;
                          case 'IN_PROGRESS':
                            bgColor = 'bg-indigo-50';
                            textColor = 'text-indigo-700';
                            break;
                          case 'REVIEW':
                            bgColor = 'bg-purple-50';
                            textColor = 'text-purple-700';
                            break;
                          case 'DELIVERED':
                            bgColor = 'bg-green-50';
                            textColor = 'text-green-700';
                            break;
                          case 'BILLED':
                            bgColor = 'bg-yellow-50';
                            textColor = 'text-yellow-700';
                            break;
                          case 'PAYMENT_RECEIVED':
                            bgColor = 'bg-emerald-50';
                            textColor = 'text-emerald-700';
                            break;
                          case 'NON_ACTIVE':
                            bgColor = 'bg-slate-100';
                            textColor = 'text-slate-700';
                            break;
                          default:
                            bgColor = 'bg-white';
                            textColor = 'text-gray-700';
                        }

                        // Return option object with both value and label, plus the styling classes
                        return {
                          value: statusOption.value,
                          label: displayValue,
                          className: `${bgColor} ${textColor} font-medium`,
                          disabled: statusOption.disabled,
                        };
                      }
                    )}
                    icon={<BsListCheck />}
                    labelClassName="text-[#8D99A5] text-xs"
                  />
                </div>
                {/* Help tooltip - moved to be inline with the field */}
                <button
                  type="button"
                  onClick={() => setShowStatusHelpModal(true)}
                  className="text-blue-500 hover:text-blue-600 transition-colors mt-2"
                  title="View Status Transition Rules"
                >
                  <FiHelpCircle size={20} />
                </button>
              </div>
            </div>
            {/* Cost input field - only show if user has finance permission */}
            {hasFinanceAccess && (
              <div className="col-span-2">
                <InputField
                  label="Project Cost"
                  type="number"
                  value={projectCost}
                  onChange={e => setProjectCost(e.target.value)}
                  placeholder="Enter project cost"
                  icon={<BsCurrencyRupee />}
                />
              </div>
            )}
          </div>

          <div className="mt-2 sm:mt-4">
            <label className="block text-[#8D99A5] text-xs mb-2">Project Image</label>
            <ImageUploader
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              previewUrl={imagePreview}
              title="Drop project image here, or click to upload"
              subTitle="Recommended size: 1200×630px"
              dragActiveTitle="Drop project image here"
              height="h-40 sm:h-48 md:h-56"
            />
          </div>
        </div>

        <div className="mt-6 sm:mt-8 flex justify-center">
          <button
            onClick={handleSubmit}
            className={`px-4 sm:px-6 py-2 sm:py-3 text-base sm:text-lg font-semibold rounded-lg transition-colors 
            flex items-center border-2 w-full justify-center gap-2 ${projectData.isValid
                ? 'bg-lime-100 border-lime-600 text-lime-700 hover:bg-lime-200'
                : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            disabled={!projectData.isValid || isSubmitting || loading}
          >
            {isSubmitting ? 'Creating...' : 'Add New Project'}
          </button>
        </div>
      </div>

      {/* Status Transition Help Modal */}
      <StatusTransitionHelpModal
        isOpen={showStatusHelpModal}
        onClose={() => setShowStatusHelpModal(false)}
      />
    </ComponentWrapper>
  );
};

export default AddProject;
